import { useState, useCallback, useRef, useMemo } from "react";
import { Row, Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon } from "./icons";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import styled from "styled-components";
import { getProcountorIntegrityCheck } from "../api";
import { getLocaleText } from "../actions/datagrid";
import { defaultColDef, sideBar, statusBarSelected, multiRow, contextMenu } from "./grid";
import Loading from "./loaders/loading";
import { useQuery } from "@tanstack/react-query";

const SCOPE_IDS = {
  PERSONAL_DETAILS: "PERSONAL_DETAILS",
  EMPLOYMENT_DETAILS: "EMPLOYMENT_DETAILS",
  SALARY_DETAILS: "SALARY_DETAILS",
  PERSONS_LIST: "PERSONS_LIST"
};

const ALL_SCOPE_IDS = Object.values(SCOPE_IDS);

const STATUS_COLOR_MAP = {
  MISMATCHED_VALUE: 'text-warning',
  default: 'text-danger'
};

const COMMON_HEIGHT = '54px';

const ScopeContainer = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid var(--bs-btn-border-color);
  border-radius: 0.375rem;
  overflow: hidden;
  width: fit-content;
  background-color: var(--blank);

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    flex-direction: unset;
  }
`;

const ScopeItem = styled.div`
  height: ${COMMON_HEIGHT};
  padding: 0 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  user-select: none;
  transition: background-color 0.2s, color 0.2s;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.2;

  font-weight: ${props => props.$isSelected ? 600 : 400};
  background-color: ${props => props.$isSelected ? 'var(--heeros-blue-200)' : 'var(--blank)'};
  color: ${props => props.$isSelected ? 'var(--heeros-blue-600-brand)' : 'var(--heeros-primary-gray-600)'};

  border-right: 1px solid var(--bs-btn-border-color);
  &:last-child {
    border-right: none;
  }

  &:hover {
    background-color: ${props => !props.$isSelected && 'var(--heeros-light)'};
  }

  i {
    font-size: 0.9em;
    color: inherit;
  }

  @media (max-width: 768px) {
    width: 100%;
    border: none;

    &:nth-child(odd) {
      border-right: 1px solid var(--bs-btn-border-color);
    }

    &:nth-child(-n+2) {
      border-bottom: 1px solid var(--bs-btn-border-color);
    }

    i {
        display: none;
    }
  }
`;

const StyledActionButton = styled(Button)`
  height: ${COMMON_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;

  @media (max-width: 768px) {
    width: 100%;
    height: 48px;
  }
`;

const ActionButtonContainer = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 10px;
  }
`;

const getStatusTranslationKey = (status) => {
  const statusMap = {
    MISMATCHED_VALUE: "StatusMismatch",
    MISSING_IN_HR: "StatusMissingHR",
    MISSING_OR_INACTIVE_IN_HR: "StatusMissingInactiveHR",
    MISSING_IN_PROCOUNTOR: "StatusMissingPC",
    MISSING_OR_INACTIVE_IN_PROCOUNTOR: "StatusMissingInactivePC",
    NOT_EXIST_IN_HR: "StatusNotExistHR",
    NOT_EXIST_IN_PROCOUNTOR: "StatusNotExistPC"
  };
  return statusMap[status];
};

const ScopeSelector = ({ scopes, selectedScopes, onScopeChange, t }) => (
  <div className="w-100 w-md-auto">
    <label className="fw-bold text-muted text-uppercase mb-2" style={{fontSize: '0.75rem', letterSpacing: '0.5px', color: 'var(--label-color)'}}>
      {t("SelectScope", "Valitse tarkistuksen laajuus")}:
    </label>

    <ScopeContainer>
      {scopes.map((scope, index) => {
        const isSelected = selectedScopes.includes(scope.id);
        const isLast = index === scopes.length - 1;

        return (
          <ScopeItem
            key={scope.id}
            onClick={() => onScopeChange(scope.id)}
            $isSelected={isSelected}
            $isLast={isLast}
          >
            <span>{scope.label}</span>
          </ScopeItem>
        );
      })}
    </ScopeContainer>
  </div>
);

const EmptyState = ({ t }) => (
  <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
    <CheckCircleIcon size="3x" className="mb-3 text-success" />
    <h4>{t("NoIntegrityIssuesFound", "Ei havaittu virheitä!")}</h4>
  </div>
);

export function ProcountorInvalidationModal({ hideModal }) {
  const { t } = useTranslation();
  const gridRef = useRef();

  const scopes = useMemo(() => [
    {
      id: SCOPE_IDS.PERSONAL_DETAILS,
      label: t("PersonalDetails", "Personal Details")
    },
    {
      id: SCOPE_IDS.EMPLOYMENT_DETAILS,
      label: t("EmploymentDetails", "Employment Details")
    },
    {
      id: SCOPE_IDS.SALARY_DETAILS,
      label: t("SalaryDetails", "Salary Details")
    },
    {
      id: SCOPE_IDS.PERSONS_LIST,
      label: t("PersonsList", "Persons List")
    }
  ], [t]);

  const [selectedScopes, setSelectedScopes] = useState(ALL_SCOPE_IDS);
  const [executionScopes, setExecutionScopes] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const { data: integrityData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["procountorIntegrityCheck", executionScopes],
    queryFn: () => getProcountorIntegrityCheck(executionScopes),
    enabled: !!executionScopes,
    refetchOnWindowFocus: false,
  });

  const rows = integrityData?.data || [];

  const handleScopeChange = useCallback((scopeId) => {
    setSelectedScopes(prev =>
      prev.includes(scopeId)
        ? prev.filter(id => id !== scopeId)
        : [...prev, scopeId]
    );
  }, []);

  const handleCheckIntegrity = useCallback(() => {
    setExecutionScopes([...selectedScopes]);
    refetch();
  }, [selectedScopes, refetch]);

  const getRowId = useCallback(({ data }) =>
    `${data.personNumber}_${data.propertyName}_${data.status}`, []
  );

  const getStatusLabel = useCallback((status) => {
    const key = getStatusTranslationKey(status);
    return key ? t(key, status) : status;
  }, [t]);

  const columnDefs = useMemo(() => [
    {
      headerName: t("Name", "Nimi"),
      field: "personName",
      rowGroup: true,
      hide: true,
    },
    {
      headerName: t("Status", "Tila"),
      field: "status",
      valueFormatter: p => getStatusLabel(p.value),
      width: 220,
      cellRenderer: params => {
        const color = STATUS_COLOR_MAP[params.value] || STATUS_COLOR_MAP.default;
        return <span className={`fw-bold ${color}`}>{params.valueFormatted}</span>;
      }
    },
    {
      headerName: t("Field", "Kenttä"),
      field: "propertyName",
      valueFormatter: p => t(p.value, p.value),
      width: 150,
    },
    {
      headerName: t("ValueInHR", "Arvo HR:ssä"),
      field: "hrValue",
      flex: 1,
      cellStyle: { color: '#666' }
    },
    {
      headerName: t("ValueInProcountor", "Arvo Procountorissa"),
      field: "procountorValue",
      flex: 1,
    },
    {
      field: "errorCount",
      headerName: t("Count", "Määrä"),
      valueGetter: () => 1,
      aggFunc: "sum",
      hide: true,
    }
  ], [t, getStatusLabel]);

  const getContextMenuItems = useCallback(params => contextMenu(t, params), [t]);

  const handleSelectionChanged = useCallback(({ api }) =>
    setSelectedRows(api.getSelectedRows()), []
  );

  const isCheckDisabled = selectedScopes.length === 0 || isFetching;
  const showLoading = isLoading || isFetching;
  const showEmptyState = !showLoading && executionScopes && rows.length === 0;
  const showGrid = !showLoading && rows.length > 0;

  return (
    <Modal show onHide={hideModal} backdrop="static" size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>{t("ProcountorIntegrityCheck", "Procountor - Erot ja virheet")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4 bg-white p-4 rounded shadow-sm border">
          <div className="d-flex justify-content-between align-items-start align-items-md-end flex-column flex-md-row gap-3">
            <ScopeSelector
              scopes={scopes}
              selectedScopes={selectedScopes}
              onScopeChange={handleScopeChange}
              t={t}
            />

            <ActionButtonContainer>
              <StyledActionButton
                variant="primary"
                onClick={handleCheckIntegrity}
                disabled={isCheckDisabled}
                className="px-4"
              >
                {isFetching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t("Checking...", "Tarkistetaan...")}
                  </>
                ) : (
                  <>
                    <i className="fa fa-play me-2"></i>
                    {t("CheckIntegrity", "Tarkista tiedot")}
                  </>
                )}
              </StyledActionButton>
            </ActionButtonContainer>
          </div>
        </div>

        <Row className="styles-ag flex-grow-1 ag-theme-material" style={{ height: "500px" }}>
          {showLoading && (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Loading show={true} />
            </div>
          )}

          {showEmptyState && <EmptyState t={t} />}

          {showGrid && (
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              autoGroupColumnDef={{
                headerName: t("Employee", "Työntekijä"),
                minWidth: 150,
                cellRendererParams: {
                  suppressCount: false,
                  innerRenderer: (params) => (
                    <span style={{fontWeight: 600}}>{params.value}</span>
                  )
                },
              }}
              defaultColDef={{
                ...defaultColDef,
                resizable: true,
                sortable: true,
                flex: 1,
              }}
              rowData={rows}
              rowSelection={multiRow}
              onSelectionChanged={handleSelectionChanged}
              getRowId={getRowId}
              sideBar={sideBar}
              statusBar={statusBarSelected}
              getContextMenuItems={getContextMenuItems}
              getLocaleText={params => getLocaleText(t, params)}
              animateRows
              suppressCellFocus
            />
          )}
        </Row>
      </Modal.Body>

      <Modal.Footer />
    </Modal>
  );
}
